import type { SpecSubject } from '@/domains/patterns/specs';
import {
  QueryClient,
  useSuspenseQuery,
  useQuery,
  queryOptions,
} from '@tanstack/react-query';

/**
 * Add typing to `meta` property of `useQuery` & `useSuspenseQuery`
 * Can be used to invalidate queries in scenarios where `queryKey` is too unwieldy
 */
declare module '@tanstack/react-query' {
  interface Register {
    queryMeta: {
      specSubject?: SpecSubject;
    };
  }
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
    },
  },
});

/**
 * Create React Query functions for a given function.
 */
export function createReactQueryFns<
  Fn extends (...args: unknown[]) => unknown,
  Opt extends Parameters<Fn>[0],
  Result extends Awaited<ReturnType<Fn>>,
>({ fn }: { fn: (opt: Opt) => Promise<Result> }) {
  if (!fn.name) throw new Error(`Must pass in a named function!`);
  const createOptions = (opt: Opt) =>
    queryOptions({
      queryKey: [fn.name, opt],
      queryFn: () => fn(opt),
    });

  return {
    createOptions,
    useFnSuspense(opt: Opt) {
      const { data, error } = useSuspenseQuery<Result>(createOptions(opt));
      if (error) throw error;
      return data;
    },
    useFn(opt: Opt) {
      return useQuery<Result>(createOptions(opt));
    },
    /**
     * Can be used outside of React components.
     * Will prime cache.
     */
    async get(opt: Opt) {
      return queryClient.fetchQuery<Result>(createOptions(opt));
    },
  };
}

/**
 * Global collection of React Query keys.
 *
 * @see https://tanstack.com/query/latest/docs/framework/react/guides/query-keys
 * @see https://tkdodo.eu/blog/effective-react-query-keys
 *
 * Example usage:
 * ```ts
 * const { data } = useQuery({
 *   queryKey: queryKeys.branches.info({ siteId, branch }),
 *   queryFn: () => appApiGql.branchInfo({ siteId, branch }),
 * });
 * ```
 *
 * Can invalidate queries like this:
 *
 * ```ts
 * queryClient.invalidateQueries({
 *   queryKey: queryKeys.branches.info({ siteId, branch }),
 * });
 * ```
 */
export const queryKeys = {
  site: {
    basicInfo: (keys: { siteId: string }) => [
      { ...keys, scope: 'siteBasicInfo' },
    ],
  },
  branches: {
    /**
     * Use this key to invalidate all branch info queries.
     * @example
     * queryClient.invalidateQueries({ queryKey: queryKeys.branches.all });
     */
    all: [{ scope: 'branchInfo' }],
    info: (keys: { siteId: string; branch: string }) => [
      { ...queryKeys.branches.all[0], ...keys },
    ],
  },
  instances: {
    /**
     * Use this key to invalidate all instance info queries.
     * @example
     * queryClient.invalidateQueries({ queryKey: queryKeys.instances.all });
     */
    all: [{ scope: 'instanceInfo' }],
    info: (keys: { instanceId: string }) => [
      { ...queryKeys.instances.all[0], ...keys },
    ],
  },
  specs: {
    all: [{ scope: 'spec' }],
    get: (keys: {
      hasInspectEndpoint: boolean;
      appClientUrl: string;
      subject: SpecSubject;
    }) => [
      {
        ...queryKeys.specs.all[0],
        ...keys,
      },
    ],
  },
  /**
   * Query keys for users
   */
  users: {
    /**
     * Use this key to invalidate all invited users queries.
     * @example
     * queryClient.invalidateQueries({ queryKey: queryKeys.users.invitedUsers });
     */
    invitedUsers: ['invitedUsers'] as const,
  },
} as const;
